/**
 * Shared procurement category option list — same merge rules as Procurement page filter.
 */
import {
  PROCUREMENT_CATEGORY_EXCLUSIONS,
  getOfficialProcurementCategoryOptions,
  toCategoryKeyForApi,
  toOfficialCategoryName,
} from '@/utils/edtechCategoryNames';
import { tenantHasProcurementCategories } from '@/lib/procurementCompetitors';
import {
  splitDealCategoryString,
  normalizeCategoryValue,
  getDealOptionCategories,
} from '@/utils/procurementCategoryUtils';

const API_BASE_URL =
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
    ? 'https://api.neogtm.com'
    : 'https://api.neogtm.com';

/** Coerce API/filter values to a string array (handles non-array bootstrap shapes). */
function toCategoryList(value) {
  if (Array.isArray(value)) return value;
  if (value == null || value === '') return [];
  if (typeof value === 'string') return [value];
  if (typeof value === 'object') {
    if (Array.isArray(value.categories)) return value.categories;
    return Object.values(value).filter((v) => typeof v === 'string');
  }
  return [];
}

function normalizeCategoryParts(value) {
  return toCategoryList(value).flatMap((c) =>
    splitDealCategoryString(c).map(normalizeCategoryValue).filter(Boolean),
  );
}

function canonicalOptionLabel(value) {
  return toOfficialCategoryName(value) || String(value || '').trim();
}

function uniqueOfficialCategories(values) {
  const seen = new Set();
  const out = [];
  for (const value of values || []) {
    const official = canonicalOptionLabel(value);
    if (!official) continue;
    const key = toCategoryKeyForApi(official) || official.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(official);
  }
  return out;
}

/**
 * Build sorted category labels for filters / Product Fit (matches Procurement.jsx).
 * @param {string[]|unknown} apiCategories - bootstrap.filters.categories
 * @param {Array<string|object>} dealSources - deal objects or normalized category strings
 */
export function buildProcurementCategoryOptions(apiCategories = [], dealSources = []) {
  const excluded = new Set(PROCUREMENT_CATEGORY_EXCLUSIONS);
  const fromApi = normalizeCategoryParts(apiCategories);
  const fromDeals = [];
  const sources = Array.isArray(dealSources) ? dealSources : [];
  for (const source of sources) {
    if (typeof source === 'string') {
      splitDealCategoryString(source)
        .map(normalizeCategoryValue)
        .filter(Boolean)
        .forEach((c) => fromDeals.push(c));
    } else if (source && typeof source === 'object') {
      // [2026-10-17] getDealOptionCategories, NOT getDealCategories: the union includes
      // `matched_categories`, which is uncontrolled free text and would add 344 junk options.
      // See the note on that helper. Matching still uses the full union.
      getDealOptionCategories(source).forEach((c) => fromDeals.push(c));
    }
  }

  return uniqueOfficialCategories([...fromApi, ...fromDeals])
    .filter((c) => c && !excluded.has(c))
    .sort((a, b) => (a || '').localeCompare(b || '', undefined, { sensitivity: 'base' }));
}

function bootstrapDealItems(bootstrap) {
  const live = bootstrap?.live ?? bootstrap?.live_deals;
  const closed = bootstrap?.closed ?? bootstrap?.closed_deals;
  return [...(live?.items || []), ...(closed?.items || [])];
}

/**
 * Tenant-scoped categories from procurement bootstrap (filters + deal categories).
 * Falls back to static official taxonomy if the request fails.
 */
export async function fetchProcurementCategoryOptionsForTenant(tenantId) {
  if (!tenantId) {
    return { options: getOfficialProcurementCategoryOptions(), source: 'static' };
  }

  if (!tenantHasProcurementCategories(tenantId)) {
    return { options: [], source: 'tenant-disabled' };
  }

  try {
    const res = await fetch(
      `${API_BASE_URL}/api/procurement/bootstrap?liveLimit=800&closedLimit=800`,
      {
        headers: { 'x-tenant-id': String(tenantId) },
        credentials: 'include',
      },
    );
    if (!res.ok) {
      console.warn('[procurementCategoryOptions] bootstrap failed:', res.status);
      return { options: getOfficialProcurementCategoryOptions(), source: 'static', error: res.status };
    }

    const bootstrap = await res.json();
    const apiCategories = toCategoryList(bootstrap?.filters?.categories);
    const deals = bootstrapDealItems(bootstrap);
    const options = buildProcurementCategoryOptions(apiCategories, deals);

    if (options.length === 0) {
      return { options: getOfficialProcurementCategoryOptions(), source: 'static-empty-bootstrap' };
    }

    return {
      options,
      source: 'bootstrap',
      apiCategoryCount: apiCategories.length,
      dealCount: deals.length,
    };
  } catch (err) {
    console.warn('[procurementCategoryOptions] bootstrap error:', err);
    return { options: getOfficialProcurementCategoryOptions(), source: 'static', error: err?.message };
  }
}

/** Merge saved profile categories that are not in the current option list (legacy labels). */
export function mergeCategoryOptionsWithSaved(options, savedCategories = []) {
  const excluded = new Set(PROCUREMENT_CATEGORY_EXCLUSIONS);
  return uniqueOfficialCategories([...(options || []), ...(savedCategories || [])])
    .filter((c) => c && !excluded.has(c))
    .sort((a, b) => (a || '').localeCompare(b || '', undefined, { sensitivity: 'base' }));
}
